import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-overview-type-2';

type SnOverviewAdvantage = {
  iconName: string;
  title: string;
  description: string;
};

type SnOverviewBlock = {
  logoUrl?: string;
  logoText: string;
  capterraRating: string;
  easeOfUseRating: string;
  customerServiceRating: string;
  pricingAmount: string;
  pricingPeriod: string;
  pricingPlan: string;
  platformsText: string;
  supportChannels: string;
  freeTrial: string;
  priceDescription: string;
  advantages: SnOverviewAdvantage[];
};

/**
 * Side-by-side product overview comparison block for signNow alternative pages.
 * Displays 1–2 stacked brand blocks, each with a logo, four key-metric stat cards
 * (Capterra rating, ease of use, customer service, pricing), a two-column specs summary,
 * and a two-column advantages grid with icon, title, and description per item.
 * Type-2 differs from type-1 by rendering rich structured data cards and advantages
 * columns rather than a plain heading and paragraph layout.
 *
 * @category SignNow
 * @slot heading - <me-heading variant="4"> - Section heading, e.g. "Overview signNow alternatives"
 * @slot subtitle - <me-text> - Section subtitle beneath the heading
 * @example
 *   <sn-overview-type-2 blocks='[{"logoText":"signNow","capterraRating":"4.6","easeOfUseRating":"4.5","customerServiceRating":"4.3","pricingAmount":"$8","pricingPeriod":"per user\nper month","pricingPlan":"signNow Business","platformsText":"web, iOS, Android","supportChannels":"tickets, live chat, phone, help center","freeTrial":"7 days","priceDescription":"$8 per user/month for Business.","advantages":[{"iconName":"shapes--eye","title":"Overview","description":"signNow is an award-winning eSignature solution."}]}]'>
 *     <me-heading slot="heading" variant="4" tag="h2">Overview signNow alternatives</me-heading>
 *     <me-text slot="subtitle">Increase your productivity by switching to a secure eSignature solution.</me-text>
 *   </sn-overview-type-2>
 */
@Component({
  tag: 'sn-overview-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnOverviewType2 {
  /**
   * Competitor overview blocks. Pass 1–2 entries; the first entry is always the primary product (signNow).
   * Each block shows a logo, four stat cards, a specs row, and a two-column advantages section.
   *
   * @type {array}
   * @block.type {object}
   * @block.property {string} logoUrl - Optional URL to brand logo image; falls back to logoText when absent
   * @block.property {string} logoText - Brand name used as fallback text logo and as img alt text
   * @block.property {string} capterraRating - Capterra score displayed with a star icon, e.g. "4.6"
   * @block.property {string} easeOfUseRating - Ease-of-use score displayed with a star icon, e.g. "4.5"
   * @block.property {string} customerServiceRating - Customer service score displayed with a star icon, e.g. "4.3"
   * @block.property {string} pricingAmount - Starting price with currency symbol, e.g. "$8"
   * @block.property {string} pricingPeriod - Period label below the price, e.g. "per user per month"
   * @block.property {string} pricingPlan - Plan name shown under the period, e.g. "signNow Business"
   * @block.property {string} platformsText - Comma-separated available platforms, e.g. "web, iOS, Android"
   * @block.property {string} supportChannels - Comma-separated support channels, e.g. "tickets, live chat, phone"
   * @block.property {string} freeTrial - Free trial duration, e.g. "7 days"
   * @block.property {string} priceDescription - Full pricing description paragraph for the right specs column
   * @block.property {array} advantages - Advantage items for this block; each item has iconName, title, description
   * @block.required logoText
   * @block.required capterraRating
   * @block.required easeOfUseRating
   * @block.required customerServiceRating
   * @block.required pricingAmount
   * @block.required pricingPeriod
   * @block.required pricingPlan
   * @block.required platformsText
   * @block.required supportChannels
   * @block.required freeTrial
   * @block.required priceDescription
   * @block.required advantages
   * @minItems 1
   * @maxItems 2
   */
  @Prop({ mutable: true }) blocks!: Array<SnOverviewBlock> | string;

  componentWillLoad() {
    this.blocks = parseJsonProp(this.blocks);
  }

  private get blockList(): SnOverviewBlock[] {
    return Array.isArray(this.blocks) ? this.blocks : [];
  }

  private renderAdvantage(adv: SnOverviewAdvantage) {
    return (
      <div class={`${COMPONENT_NAME}__advantage`}>
        <div class={`${COMPONENT_NAME}__advantage-title`}>
          <me-icon
            name={adv.iconName}
            class={`${COMPONENT_NAME}__advantage-icon`}
            width="32"
            height="32"
            lazy={false}
          />
          {adv.title}
        </div>
        <p class={`${COMPONENT_NAME}__advantage-desc`}>{adv.description}</p>
      </div>
    );
  }

  private renderBlock(block: SnOverviewBlock, isPrimary: boolean) {
    const advantages = block.advantages || [];
    const mid = Math.ceil(advantages.length / 2);
    const leftAdvantages = advantages.slice(0, mid);
    const rightAdvantages = advantages.slice(mid);

    return (
      <div class={`${COMPONENT_NAME}__block${!isPrimary ? ` ${COMPONENT_NAME}__block--competitor` : ''}`}>
        <div class={`${COMPONENT_NAME}__logo`}>
          {block.logoUrl ? (
            <img src={block.logoUrl} alt={block.logoText} class={`${COMPONENT_NAME}__logo-img`} loading="lazy" />
          ) : (
            <span class={`${COMPONENT_NAME}__logo-text`}>{block.logoText}</span>
          )}
        </div>

        <div class={`${COMPONENT_NAME}__stats`} role="list">
          <div class={`${COMPONENT_NAME}__stat`} role="listitem">
            <div class={`${COMPONENT_NAME}__stat-inner`}>
              <div class={`${COMPONENT_NAME}__stat-icon`} aria-hidden="true">
                <me-icon class={`${isPrimary ? 'primary' : 'competitor'}`} name="signnow--capterra" lazy={false}></me-icon>
              </div>
              <div class={`${COMPONENT_NAME}__stat-title`}>Capterra rating</div>
              <div class={`${COMPONENT_NAME}__stat-rating`}>
                <me-icon class={`${isPrimary ? 'primary' : 'competitor'}`} name="shapes--star-no-borders" lazy={false}></me-icon>
                <strong class={`${COMPONENT_NAME}__stat-rating-value`}>{block.capterraRating}</strong>&nbsp;/ 5
              </div>
            </div>
          </div>

          <div class={`${COMPONENT_NAME}__stat`} role="listitem">
            <div class={`${COMPONENT_NAME}__stat-inner`}>
              <div class={`${COMPONENT_NAME}__stat-icon`} aria-hidden="true">
                <me-icon class={`${isPrimary ? 'primary' : 'competitor'}`} name="signnow--easeOf" lazy={false}></me-icon>
              </div>
              <div class={`${COMPONENT_NAME}__stat-title`}>Ease of use</div>
              <div class={`${COMPONENT_NAME}__stat-rating`}>
                <me-icon class={`${isPrimary ? 'primary' : 'competitor'}`} name="shapes--star-no-borders" lazy={false}></me-icon>
                <strong class={`${COMPONENT_NAME}__stat-rating-value`}>{block.easeOfUseRating}</strong>&nbsp;/ 5
              </div>
            </div>
          </div>

          <div class={`${COMPONENT_NAME}__stat`} role="listitem">
            <div class={`${COMPONENT_NAME}__stat-inner`}>
              <div class={`${COMPONENT_NAME}__stat-icon`} aria-hidden="true">
                <me-icon class={`${isPrimary ? 'primary' : 'competitor'}`} name="signnow--services" lazy={false}></me-icon>
              </div>
              <div class={`${COMPONENT_NAME}__stat-title`}>Customer service</div>
              <div class={`${COMPONENT_NAME}__stat-rating`}>
                <me-icon class={`${isPrimary ? 'primary' : 'competitor'}`} name="shapes--star-no-borders" lazy={false}></me-icon>
                <strong class={`${COMPONENT_NAME}__stat-rating-value`}>{block.customerServiceRating}</strong>&nbsp;/ 5
              </div>
            </div>
          </div>

          <div class={`${COMPONENT_NAME}__stat`} role="listitem">
            <div class={`${COMPONENT_NAME}__stat-inner`}>
              <div class={`${COMPONENT_NAME}__stat-icon`} aria-hidden="true">
                <me-icon class={`${isPrimary ? 'primary' : 'competitor'}`} name="signnow--pricing" lazy={false}></me-icon>
              </div>
              <div class={`${COMPONENT_NAME}__stat-title`}>Pricing</div>
              <div class={`${COMPONENT_NAME}__stat-price`}>
                <div class={`${COMPONENT_NAME}__stat-price-amount`}>
                  <strong class={`${COMPONENT_NAME}__stat-rating-value`}>{block.pricingAmount}</strong>
                  <span>{block.pricingPeriod}</span>
                </div>
                <div class={`${COMPONENT_NAME}__stat-plan`}>{block.pricingPlan}</div>
              </div>
            </div>
          </div>
        </div>

        <div class={`${COMPONENT_NAME}__specs`}>
          <div class={`${COMPONENT_NAME}__specs-col`}>
            <p class={`${COMPONENT_NAME}__spec`}>
              <strong>Available platforms:</strong> {block.platformsText}
            </p>
            <p class={`${COMPONENT_NAME}__spec`}>
              <strong>Support:</strong> {block.supportChannels}
            </p>
            <p class={`${COMPONENT_NAME}__spec`}>
              <strong>Free trial:</strong> {block.freeTrial}
            </p>
          </div>
          <div class={`${COMPONENT_NAME}__specs-col`}>
            <p class={`${COMPONENT_NAME}__spec`}>
              <strong>Price:</strong> {block.priceDescription}
            </p>
          </div>
        </div>

        {advantages.length > 0 && (
          <div class={`${COMPONENT_NAME}__advantages`}>
            <div class={`${COMPONENT_NAME}__advantages-col`}>{leftAdvantages.map(adv => this.renderAdvantage(adv))}</div>
            <div class={`${COMPONENT_NAME}__advantages-col`}>{rightAdvantages.map(adv => this.renderAdvantage(adv))}</div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const blocks = this.blockList;
    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__header`}>
          <slot name="heading" />
          <slot name="subtitle" />
        </div>
        {blocks.map((block, index) => this.renderBlock(block, index === 0))}
      </me-section>
    );
  }
}
